import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { RoomGamePrizeSummary, RoomGameType, RoomGamesConfigResponse } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

type DraftPrize = {
  gameType: RoomGameType;
  label: string;
  weight: string;
};

const emptyDraft = (gameType: RoomGameType): DraftPrize => ({
  gameType,
  label: '',
  weight: '10',
});

export function ModelGamesCard(): ReactElement {
  const { api } = useAuth();
  const [config, setConfig] = useState<RoomGamesConfigResponse | null>(null);
  const [draft, setDraft] = useState<DraftPrize>(emptyDraft('WHEEL'));
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const result = await api.getMyRoomGames();
    setConfig(result);
  }, [api]);

  useEffect(() => {
    void load()
      .catch(() => {
        setError(t('common.studio.games.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [load]);

  async function saveSettings(
    patch: Partial<{
      wheelEnabled: boolean;
      wheelCostCoins: number;
      slotEnabled: boolean;
      slotCostCoins: number;
      raffleEnabled: boolean;
      raffleCostCoins: number;
      crownEnabled: boolean;
      crownCostCoins: number;
      highRollerEnabled: boolean;
      highRollerMinCoins: number;
    }>,
  ): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      const result = await api.updateMyRoomGames(patch);
      setConfig(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.games.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleAddPrize(): Promise<void> {
    const weight = Number.parseInt(draft.weight, 10);
    if (!draft.label.trim() || !Number.isFinite(weight) || weight < 1) {
      setError(t('common.studio.games.invalidPrize'));
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await api.createRoomGamePrize({
        gameType: draft.gameType,
        label: draft.label.trim(),
        weight,
      });
      setDraft(emptyDraft(draft.gameType));
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.games.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function togglePrize(prize: RoomGamePrizeSummary): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.updateRoomGamePrize(prize.id, { enabled: !prize.enabled });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.games.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function removePrize(prizeId: string): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.deleteRoomGamePrize(prizeId);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.games.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.games.loading')}</p>;
  }

  if (!config) {
    return <p className="fm-error">{t('common.studio.games.loadError')}</p>;
  }

  const wheelPrizes = config.prizes.filter((prize) => prize.gameType === 'WHEEL');
  const slotPrizes = config.prizes.filter((prize) => prize.gameType === 'SLOT');

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.games.title')}</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.games.hint')}
      </p>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.8125rem' }}>
        {t('common.studio.games.competitiveComingSoon')}
      </p>

      {error ? <p className="fm-error">{error}</p> : null}

      <div style={{ display: 'grid', gap: '1rem' }}>
        <GameSettingsBlock
          title={t('common.studio.games.wheel')}
          enabled={config.settings.wheelEnabled}
          costCoins={config.settings.wheelCostCoins}
          prizes={wheelPrizes}
          disabled={isSaving}
          onToggleEnabled={(enabled) => {
            void saveSettings({ wheelEnabled: enabled });
          }}
          onCostChange={(cost) => {
            void saveSettings({ wheelCostCoins: cost });
          }}
          onTogglePrize={(prize) => {
            void togglePrize(prize);
          }}
          onDeletePrize={(id) => {
            void removePrize(id);
          }}
        />

        <GameSettingsBlock
          title={t('common.studio.games.slot')}
          enabled={config.settings.slotEnabled}
          costCoins={config.settings.slotCostCoins}
          prizes={slotPrizes}
          disabled={isSaving}
          onToggleEnabled={(enabled) => {
            void saveSettings({ slotEnabled: enabled });
          }}
          onCostChange={(cost) => {
            void saveSettings({ slotCostCoins: cost });
          }}
          onTogglePrize={(prize) => {
            void togglePrize(prize);
          }}
          onDeletePrize={(id) => {
            void removePrize(id);
          }}
        />

        <SimpleGameToggleBlock
          title={t('common.studio.games.raffle')}
          enabled={config.settings.raffleEnabled}
          costCoins={config.settings.raffleCostCoins}
          costLabel={t('common.studio.games.cost')}
          disabled={isSaving}
          onToggleEnabled={(enabled) => {
            void saveSettings({ raffleEnabled: enabled });
          }}
          onCostChange={(cost) => {
            void saveSettings({ raffleCostCoins: cost });
          }}
        />

        <SimpleGameToggleBlock
          title={t('common.studio.games.crown')}
          enabled={config.settings.crownEnabled}
          costCoins={config.settings.crownCostCoins}
          costLabel={t('common.studio.games.cost')}
          disabled={isSaving}
          onToggleEnabled={(enabled) => {
            void saveSettings({ crownEnabled: enabled });
          }}
          onCostChange={(cost) => {
            void saveSettings({ crownCostCoins: cost });
          }}
        />

        <SimpleGameToggleBlock
          title={t('common.studio.games.highRoller')}
          enabled={config.settings.highRollerEnabled}
          costCoins={config.settings.highRollerMinCoins}
          costLabel={t('common.studio.games.highRollerMin')}
          disabled={isSaving}
          onToggleEnabled={(enabled) => {
            void saveSettings({ highRollerEnabled: enabled });
          }}
          onCostChange={(cost) => {
            void saveSettings({ highRollerMinCoins: cost });
          }}
        />
      </div>

      <div style={{ marginTop: '1.25rem', display: 'grid', gap: '0.5rem' }}>
        <label>
          {t('common.studio.games.prizeLabel')}
          <input
            className="fm-input"
            value={draft.label}
            onChange={(event) => {
              setDraft((current) => ({ ...current, label: event.target.value }));
            }}
          />
        </label>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <label>
            Game
            <select
              className="fm-input"
              value={draft.gameType}
              onChange={(event) => {
                setDraft((current) => ({
                  ...current,
                  gameType: event.target.value as RoomGameType,
                }));
              }}
            >
              <option value="WHEEL">{t('common.studio.games.wheel')}</option>
              <option value="SLOT">{t('common.studio.games.slot')}</option>
            </select>
          </label>
          <label>
            {t('common.studio.games.weight')}
            <input
              className="fm-input"
              type="number"
              min={1}
              value={draft.weight}
              onChange={(event) => {
                setDraft((current) => ({ ...current, weight: event.target.value }));
              }}
            />
          </label>
        </div>
        <Button
          label={isSaving ? t('common.studio.games.saving') : t('common.studio.games.addPrize')}
          disabled={isSaving}
          onClick={() => {
            void handleAddPrize();
          }}
        />
      </div>
    </div>
  );
}

function SimpleGameToggleBlock({
  title,
  enabled,
  costCoins,
  costLabel,
  disabled,
  onToggleEnabled,
  onCostChange,
}: {
  title: string;
  enabled: boolean;
  costCoins: number;
  costLabel: string;
  disabled: boolean;
  onToggleEnabled: (enabled: boolean) => void;
  onCostChange: (cost: number) => void;
}): ReactElement {
  return (
    <section
      style={{
        borderTop: '1px solid color-mix(in srgb, var(--fm-color-text-muted) 25%, transparent)',
        paddingTop: '0.75rem',
      }}
    >
      <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem' }}>{title}</h3>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <input
          type="checkbox"
          checked={enabled}
          disabled={disabled}
          onChange={(event) => {
            onToggleEnabled(event.target.checked);
          }}
        />
        {t('common.studio.games.enabled')}
      </label>
      <label style={{ display: 'block' }}>
        {costLabel}
        <input
          className="fm-input"
          type="number"
          min={8}
          value={costCoins}
          disabled={disabled}
          onChange={(event) => {
            const next = Number.parseInt(event.target.value, 10);
            if (Number.isFinite(next) && next >= 8) {
              onCostChange(next);
            }
          }}
        />
      </label>
    </section>
  );
}

function GameSettingsBlock({
  title,
  enabled,
  costCoins,
  prizes,
  disabled,
  onToggleEnabled,
  onCostChange,
  onTogglePrize,
  onDeletePrize,
}: {
  title: string;
  enabled: boolean;
  costCoins: number;
  prizes: RoomGamePrizeSummary[];
  disabled: boolean;
  onToggleEnabled: (enabled: boolean) => void;
  onCostChange: (cost: number) => void;
  onTogglePrize: (prize: RoomGamePrizeSummary) => void;
  onDeletePrize: (id: string) => void;
}): ReactElement {
  return (
    <section
      style={{
        borderTop: '1px solid color-mix(in srgb, var(--fm-color-text-muted) 25%, transparent)',
        paddingTop: '0.75rem',
      }}
    >
      <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem' }}>{title}</h3>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <input
          type="checkbox"
          checked={enabled}
          disabled={disabled}
          onChange={(event) => {
            onToggleEnabled(event.target.checked);
          }}
        />
        {t('common.studio.games.enabled')}
      </label>
      <label style={{ display: 'block', marginBottom: '0.75rem' }}>
        {t('common.studio.games.cost')}
        <input
          className="fm-input"
          type="number"
          min={8}
          value={costCoins}
          disabled={disabled}
          onChange={(event) => {
            const next = Number.parseInt(event.target.value, 10);
            if (Number.isFinite(next) && next >= 8) {
              onCostChange(next);
            }
          }}
        />
      </label>
      <p style={{ margin: '0 0 0.35rem', fontSize: '0.8rem', color: 'var(--fm-color-text-muted)' }}>
        {t('common.studio.games.prizes')}
      </p>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.35rem' }}>
        {prizes.map((prize) => (
          <li
            key={prize.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.5rem',
              fontSize: '0.85rem',
              opacity: prize.enabled ? 1 : 0.55,
            }}
          >
            <span>
              {prize.label} · {t('common.studio.games.weight')} {String(prize.weight)} ·{' '}
              {t('common.studio.games.odds')} {String(Math.round(prize.odds * 100))}%
            </span>
            <span style={{ display: 'flex', gap: '0.35rem' }}>
              <button
                type="button"
                className="fm-link-button"
                disabled={disabled}
                onClick={() => {
                  onTogglePrize(prize);
                }}
              >
                {prize.enabled ? t('common.studio.games.enabled') : 'Off'}
              </button>
              <button
                type="button"
                className="fm-link-button"
                disabled={disabled}
                onClick={() => {
                  onDeletePrize(prize.id);
                }}
              >
                {t('common.studio.games.delete')}
              </button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
